<script setup>
import { computed, inject, nextTick, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'

import RHDHVButton from '@/components/common/RHDHVButton.vue'
import MooringPointTab from '@/components/mooring-points/MooringPointTab.vue'
import ShipTab from '@/components/ship/ShipTab.vue'
import { HelpTextTooltip } from '@haskoning/vue-component-library'

import { ISLAND } from '@/components/calculation/calculation.constant'

import { useOptimisationStore } from '@/stores/optimisation'
import { useCalculationStore } from '@/stores/calculation'
import { useMooringPointStore } from '@/stores/mooring_point'
import { useShipStore } from '@/stores/ship'
import { useFairleadStore } from '@/stores/fairlead'
import { useWinchStore } from '@/stores/winch'
import { useShipContourStore } from '@/stores/ship_contour'
import { useShipApiStore } from '@/stores/shipApi'
import { BERTH_TYPE } from '@/constants/calculationFieldConstants'

const shipStore = useShipStore()
const optimisationStore = useOptimisationStore()
const mooringPointStore = useMooringPointStore()
const fairleadStore = useFairleadStore()
const winchStore = useWinchStore()
const shipContourStore = useShipContourStore()
const shipApiStore = useShipApiStore()

const appHeaderHeight = inject('appHeaderHeight')

const { currentItem: currentCalculation } = storeToRefs(useCalculationStore())
const {
  fetchLoading: fetchOptimisationLoading,
  loading: optimiseLoading,
  generateLoading
} = storeToRefs(optimisationStore)
const { fetchLoading: mooringPointLoading } = storeToRefs(mooringPointStore)
const { fetchLoading: shipLoading } = storeToRefs(shipStore)
const { isAuth: shipApiIsAuth } = storeToRefs(shipApiStore)

let resizeObserver = null

const tab = ref('vessel')
const tabs = ref(null)
const action = ref(null)
const tabsHeight = ref(0)
const actionHeight = ref(0)

const mooringPointHelpText =
  'Here the coordinates (X, Y, Z) of all mooring points are shown; X and Y are relative to the ‘earth-fixed coordinate system (shown on the left) and Z is relative to CD'
const designVesselHelpText =
  'Here you can define the design vessels, from certain vessel classes/ranges with ‘typical’ vessel particulars, which can be overwritten manually '

watch(currentCalculation, async (currentState) => {
  if (currentState.id) {
    await mooringPointStore.fetchOptions()
    await fairleadStore.fetchOptions()
    await shipStore.fetchOptions()
    if (shipApiIsAuth.value) {
      await shipApiStore.fetchOptions()
    }
    await shipStore.fetchItems({ calculation: currentState.id })
    await fairleadStore.fetchItems({ ship__calculation: currentState.id })
    await mooringPointStore.fetchItems({ calculation: currentState.id })
    await winchStore.fetchItems({ ship__calculation: currentState.id })
    await shipContourStore.fetchItems({ ship__calculation: currentState.id })
  }
})

const windowItemHeight = computed(() => {
  return `calc(100vh - ${tabsHeight.value + actionHeight.value + appHeaderHeight.value}px)`
})

const updateHeight = () => {
  const getHeight = (element) => element.value?.$el?.clientHeight || 0

  tabsHeight.value = getHeight(tabs)
  actionHeight.value = getHeight(action)
}

const buttonLoading = computed(() => {
  return fetchOptimisationLoading.value || optimiseLoading.value || generateLoading.value
})

onMounted(async () => {
  await nextTick()
  if (action.value) {
    resizeObserver = new ResizeObserver(() => {
      updateHeight()
    })
    resizeObserver.observe(action.value.$el)
  }
})
</script>

<template>
  <v-card variant="text" rounded="0">
    <v-card class="pa-0" rounded="0" width="100%" variant="text">
      <v-tabs v-model="tab" fixed-tabs grow color="primary">
        <v-tab ref="tabs" rounded="0" value="vessel">
          <span class="mr-3"> Design vessels </span>
          <v-progress-circular
            v-if="shipLoading"
            size="x-small"
            indeterminate
            color="primary"
          ></v-progress-circular>
          <HelpTextTooltip v-else :text="designVesselHelpText" color="primary"></HelpTextTooltip>
        </v-tab>
        <v-tab rounded="0" value="mooring">
          <span class="mr-3"> Mooring points </span>
          <v-progress-circular
            v-if="mooringPointLoading"
            size="x-small"
            indeterminate
            color="primary"
          ></v-progress-circular>
          <HelpTextTooltip v-else :text="mooringPointHelpText" color="primary"></HelpTextTooltip>
        </v-tab>
      </v-tabs>
      <v-window v-model="tab">
        <v-window-item value="vessel">
          <ShipTab />
        </v-window-item>
        <v-window-item value="mooring">
          <MooringPointTab />
        </v-window-item>
      </v-window>
    </v-card>
    <v-card-actions ref="action" class="center" variant="flat" rounded="0">
      <RHDHVButton
        v-if="currentCalculation[BERTH_TYPE] === ISLAND"
        :loading="buttonLoading"
        width="50%"
        allowed="add_mooringpoint"
        auth-source="ProjectUser"
        variant="elevated"
        elevation="0"
        size="large"
        prepend-icon="mdi-refresh"
        @click="optimisationStore.optimise()"
      >
        Optimise
      </RHDHVButton>

      <RHDHVButton
        v-else
        :disabled="generateLoading"
        :loading="buttonLoading"
        width="50%"
        allowed="change_mooringpoint"
        auth-source="ProjectUser"
        variant="elevated"
        size="large"
        elevation="0"
        append-icon="mdi-refresh"
        @click="optimisationStore.linkToFairleads()"
      >
        Link to fairleads
      </RHDHVButton>
    </v-card-actions>
  </v-card>
</template>
<style scoped>
.v-window-item {
  height: v-bind(windowItemHeight);
  overflow-y: auto;
}

:deep(.v-tab) {
  max-width: unset !important;
}
</style>
